import { Form, Formik }         from 'formik';
import { useCallback, useMemo } from 'react';
import useSWR                   from 'swr';
import { Grid }                 from '@mui/material';

import { CommonButtonWrapper, PageContent } from '@styles/CommonStyles';
import { FormPart }          from '@modules/shared/components/FormPart';
import { ISpesificEltCode }  from '@modules/admin/Tools/LenderUtility/interfaces';

import { Modal }                   from '@shared/components/modals/Modal';
import { statesList }              from '@shared/constants/statesList';
import { SelectAutocompleteField } from '@shared/fields/SelectAutocompleteField';
import { SERVICES }                from '@shared/constants/apiConfig';
import { getOptions }              from '@shared/commonFunctions';
import { CancelButton, SaveButton } from '@shared/inputs/Buttons';
import { ZipCodeInputField }       from '@shared/fields/ZipCodeInputField';

import { lenderELTEditorValidationSchema }            from './validation';
import { createEltCodeRequest, updateEltCodeRequest } from './requests';
import { ILenderELTCode, TLenderELTSpecificEltCodes } from './interfaces';

const prefix = 'stateSpecificEltCode';

interface IAddEltCodeModalProps {
  open                  : boolean;
  currentEltCode        : TLenderELTSpecificEltCodes | null;
  refetchLenderELTCodes : () => void;
  handleClose           : () => void;
  eltCodeModalPosition: number;
}

export const AddEltCodeModal = ({
  open,
  currentEltCode,
  refetchLenderELTCodes,
  eltCodeModalPosition,
  handleClose,
}: IAddEltCodeModalProps) => {
  const { data: lenders, error } = useSWR<ILenderELTCode[]>(`/${SERVICES.LENDER}/lenders/eltcodes`);

  const initialValues = useMemo(() => ({
    stateSpecificEltCode: {
      id             : currentEltCode?.id ?? '',
      state          : currentEltCode?.state ?? null,
      newState       : currentEltCode?.state ?? null,
      name           : currentEltCode?.name ?? '',
      lenderId       : currentEltCode?.lenderId ?? '',
      eltCode        : currentEltCode?.eltCode ?? '',
      lenderName     : currentEltCode?.lenderName ?? '',
      lenderAddress  : currentEltCode?.lenderAddress ?? '',
      lenderState    : currentEltCode?.lenderState ?? null,
      lenderCity     : currentEltCode?.lenderCity ?? '',
      newLenderId    : '',
      lenderPostCode : currentEltCode?.lenderPostCode ?? '',
      isDeleted      : currentEltCode?.isDeleted ?? false,
    }
  }), [currentEltCode]);

  const sortedLenderOptions = useMemo(() => lenders?.sort((a, b) => a.name.localeCompare(b.name)), [lenders]);

  const formConfig = [
    {
      hideNoneOption : true,
      name           : `${prefix}.name`,
      label          : 'Lender',
      options        : getOptions(sortedLenderOptions, 'name'),
      isLoading      : !sortedLenderOptions && !error,
      component      : SelectAutocompleteField,
      shouldUpdate   : () => true,
    }, {
      hideNoneOption : true,
      name           : `${prefix}.newState`,
      label          : 'For State Of',
      options        : statesList,
      component      : SelectAutocompleteField,
    }, {
      name  : `${prefix}.lenderName`,
      label : 'Lender Name',
    }, {
      name  : `${prefix}.eltCode`,
      label : 'ELT Code',
    }, {
      name  : `${prefix}.lenderAddress`,
      label : 'Address',
    }, {
      hideNoneOption : true,
      name           : `${prefix}.lenderState`,
      label          : 'State',
      options        : statesList,
      component      : SelectAutocompleteField,
    }, {
      name  : `${prefix}.lenderCity`,
      label : 'City',
    }, {
      name      : `${prefix}.lenderPostCode`,
      label     : 'Zip',
      component : ZipCodeInputField
    }].map((field) => ({
      ...field,
      width: '50%',
    }));

  const handleSubmit = useCallback((values: ISpesificEltCode) => {
    const { newState,lenderState, ...restData } = values.stateSpecificEltCode;

    const newLenderId = lenders?.find((lender) => lender.name === values.stateSpecificEltCode.name)?.lenderId;

    const createRequestData = {
      stateSpecificEltCode: {
        ...restData,
        state       : newState?.length ? newState : null  ,
        lenderState: lenderState?.length ? lenderState : null,
        lenderId    : newLenderId,
        newState    : null,
        newLenderId : null
      },
    };

    const updateRequestData = {
      stateSpecificEltCode: {
        ...restData,
        ...({ newState: newState?.length ? newState : null }),
        ...({ lenderState: lenderState?.length ? lenderState : null }),
        ...({ newLenderId: newLenderId !== values.stateSpecificEltCode.lenderId ? newLenderId : null }),
      },
    };

    if (currentEltCode?.name && updateRequestData.stateSpecificEltCode.lenderId) {
      updateEltCodeRequest(updateRequestData.stateSpecificEltCode.lenderId, updateRequestData, handleClose);
    } else {
      if (createRequestData.stateSpecificEltCode.lenderId) {
        createEltCodeRequest(createRequestData.stateSpecificEltCode.lenderId, createRequestData, handleClose);
      }
    }

    refetchLenderELTCodes();
  }, [currentEltCode?.name, lenders, updateEltCodeRequest, createEltCodeRequest, refetchLenderELTCodes]);

  return (
    <Modal
      hideBackdrop
      dialogWidth = "50vw"
      title={currentEltCode?.name ? 'Update New ELT Code' : 'Add ELT Code'}
      open        = {open}
      resetPosition={eltCodeModalPosition}
      onClose     = {handleClose}
    >
      <Formik
        validateOnChange
        initialValues    = {initialValues}
        validationSchema = {lenderELTEditorValidationSchema}
        onSubmit         = {handleSubmit}
      >
        <Form>
          <PageContent>
            <Grid container spacing={12}>
              <Grid item md={12}>
                <FormPart config={formConfig} />
              </Grid>
            </Grid>
          </PageContent>

          <CommonButtonWrapper>
            <CancelButton onClick={handleClose} />
            <SaveButton />
          </CommonButtonWrapper>
        </Form>
      </Formik>
    </Modal>
  );
};
